import * as React from "react";

import { useControlled } from "../forms/useControlled";
import "./toggle-button.css";

/**
 * ToggleButton and ToggleButtonGroup, replacing `@salt-ds/core`'s behind `toggle.tsx` and
 * `toggle-group.tsx`.
 *
 * ── THE SAME BUTTON IS TWO DIFFERENT CONTROLS DEPENDING ON ITS CONTEXT ────────────────
 *
 * Standalone it is a TOGGLE: `aria-pressed`, on or off, independent of anything else.
 * Inside a group it is an OPTION: `role="radio"` with `aria-checked`, one of a set.
 *
 * That is not decoration. A screen-reader user is told "pressed" for the first and
 * "selected, 2 of 4" for the second, and those are genuinely different promises about what
 * happens to the other buttons when this one is activated. Emitting `aria-pressed` inside a
 * group would describe a set of independent switches that behave as a single choice.
 *
 * ── ARROW KEYS MOVE, TAB LEAVES ───────────────────────────────────────────────────────
 *
 * A radio group is ONE tab stop. The group owns arrow-key navigation and each button carries a
 * roving `tabIndex`, so Tab moves past the whole group rather than through every option — which
 * is what makes a twelve-option filter bar usable by keyboard rather than a twelve-stop detour.
 *
 * Disabled buttons are excluded from the arrow-key sequence (`button:not([disabled])`), so
 * navigation never parks on a control that cannot be chosen.
 */
/*
 * Read from the vendor's `.d.ts`, not inferred. Three things were wrong on the first attempt and
 * every one is a real call site:
 *
 *   Value            is `string | readonly string[] | number | undefined`. Call sites pass
 *                    ARRAYS — CreateSegmentDialog and EditCohortCriteriaDialog both do.
 *   group appearance is narrower than the button's: `"bordered" | "solid"` only.
 *   isSelected       stays a plain `value === id`. With an array value that never matches by
 *                    identity, which looks like a bug and is the vendor's actual behaviour —
 *                    "fixing" it here would change which buttons appear selected in two live
 *                    dialogs, inside a change whose whole promise is that nothing moves.
 *
 * Fifth time in this migration that reading the implementation instead of the declaration
 * produced a narrower API.
 */
export type ToggleValue = string | readonly string[] | number | undefined;
export type ToggleAppearance = "solid" | "bordered" | "transparent";
export type ToggleGroupAppearance = "solid" | "bordered";
export type ToggleSentiment = "accented" | "neutral" | "positive" | "negative" | "caution";
export type ToggleOrientation = "horizontal" | "vertical";

interface ToggleButtonGroupContextValue {
  appearance?: ToggleGroupAppearance;
  disabled?: boolean;
  focus: (id: ToggleValue) => void;
  isFocused: (id: ToggleValue) => boolean;
  isSelected: (id: ToggleValue) => boolean;
  orientation: ToggleOrientation;
  readOnly?: boolean;
  select: (event: React.SyntheticEvent<HTMLButtonElement>) => void;
  sentiment?: ToggleSentiment;
}

const ToggleButtonGroupContext = React.createContext<ToggleButtonGroupContextValue | null>(null);

export interface ToggleButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "onChange"> {
  appearance?: ToggleAppearance;
  sentiment?: ToggleSentiment;
  selected?: boolean;
  defaultSelected?: boolean;
  readOnly?: boolean;
  onChange?: (event: React.SyntheticEvent<HTMLButtonElement>) => void;
}

export const ToggleButton = React.forwardRef<HTMLButtonElement, ToggleButtonProps>(
  function ToggleButton(
    {
      appearance: appearanceProp,
      children,
      className,
      defaultSelected,
      disabled: disabledProp,
      onChange,
      onClick,
      onFocus,
      readOnly: readOnlyProp,
      selected: selectedProp,
      sentiment: sentimentProp,
      value,
      ...rest
    },
    ref,
  ) {
    const group = React.useContext(ToggleButtonGroupContext);

    const groupSelected = group ? group.isSelected(value as ToggleValue) : selectedProp;
    // Only one button in a group is tabbable; outside a group every button is.
    const focusable = group ? group.isFocused(value as ToggleValue) : true;
    const sentiment = sentimentProp || group?.sentiment || "neutral";
    const appearance = appearanceProp || group?.appearance || "solid";
    const disabled = group?.disabled || disabledProp;
    const readOnly = group?.readOnly || readOnlyProp;

    const [selected, setSelected] = useControlled<boolean>({
      controlled: groupSelected,
      default: Boolean(defaultSelected),
    });

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      // Read-only is enforced here, not just announced — a `<button>` ignores the readOnly
      // attribute entirely, so without this the control says read-only and changes anyway.
      if (disabled || readOnly) return;
      group?.select(event);
      setSelected(!selected);
      onChange?.(event);
      onClick?.(event);
    };

    const classes = [
      "saltToggleButton",
      `saltToggleButton-${sentiment}`,
      `saltToggleButton-${appearance}`,
    ];
    if (readOnly) classes.push("saltToggleButton-readOnly");
    if (className) classes.push(className);

    return (
      <button
        // Exactly one of these, never both: a toggle is "pressed", an option is "checked".
        aria-checked={group ? selected : undefined}
        aria-disabled={disabled}
        aria-pressed={!group ? selected : undefined}
        aria-readonly={readOnlyProp}
        className={classes.join(" ")}
        disabled={disabled}
        onClick={handleClick}
        onFocus={(event) => {
          group?.focus(value as ToggleValue);
          onFocus?.(event);
        }}
        ref={ref}
        role={group ? "radio" : undefined}
        tabIndex={focusable ? 0 : -1}
        // `type="button"` or a toggle inside a form submits it on every press.
        type="button"
        value={value}
        {...rest}
      >
        {children}
      </button>
    );
  },
);

export interface ToggleButtonGroupProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange" | "defaultValue"> {
  appearance?: ToggleGroupAppearance;
  sentiment?: ToggleSentiment;
  orientation?: ToggleOrientation;
  value?: ToggleValue;
  defaultValue?: ToggleValue;
  disabled?: boolean;
  readOnly?: boolean;
  onChange?: (event: React.SyntheticEvent<HTMLButtonElement>) => void;
}

export const ToggleButtonGroup = React.forwardRef<HTMLDivElement, ToggleButtonGroupProps>(
  function ToggleButtonGroup(
    {
      appearance,
      children,
      className,
      defaultValue,
      disabled,
      onChange,
      onKeyDown,
      orientation = "horizontal",
      readOnly,
      sentiment,
      value: valueProp,
      ...rest
    },
    ref,
  ) {
    const groupRef = React.useRef<HTMLDivElement | null>(null);
    const setRefs = React.useCallback(
      (node: HTMLDivElement | null) => {
        groupRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
      },
      [ref],
    );

    const [value, setValue] = useControlled<ToggleValue>({
      controlled: valueProp,
      default: defaultValue,
    });
    const [focused, setFocused] = React.useState<ToggleValue>(value);

    const select = (event: React.SyntheticEvent<HTMLButtonElement>) => {
      const next = (event.currentTarget as HTMLButtonElement).value;
      setValue(next);
      // Only when it actually changed: re-selecting the current option is not a change, and
      // reporting it would fire a filter refetch on every stray click.
      if (value !== next) onChange?.(event);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      // Disabled buttons are excluded, so arrow navigation never parks on an option that
      // cannot be chosen.
      const elements = Array.from(
        groupRef.current?.querySelectorAll<HTMLButtonElement>("button:not([disabled])") ?? [],
      );
      const doc = groupRef.current?.ownerDocument ?? document;
      const currentIndex = elements.indexOf(doc.activeElement as HTMLButtonElement);
      switch (event.key) {
        case "ArrowDown":
        case "ArrowRight":
          // Wraps, so the sequence has no dead end at either extremity.
          elements[(currentIndex + 1) % elements.length]?.focus();
          break;
        case "ArrowUp":
        case "ArrowLeft":
          elements[(currentIndex - 1 + elements.length) % elements.length]?.focus();
          break;
      }
      onKeyDown?.(event);
    };

    // Not memoised: `select` closes over the caller's `onChange`, which closes over page state.
    const contextValue: ToggleButtonGroupContextValue = {
      appearance,
      disabled,
      focus: (id) => setFocused(id),
      // `|| !focused` so that BEFORE anything has been focused, every button is tabbable and
      // the group can be entered at all — a roving tabindex with no initial holder is a group
      // no keyboard user can reach.
      isFocused: (id) => focused === id || !focused,
      isSelected: (id) => value === id,
      orientation,
      readOnly,
      select,
      sentiment,
    };

    const classes = ["saltToggleButtonGroup", `saltToggleButtonGroup-${orientation}`];
    if (disabled) classes.push("saltToggleButtonGroup-disabled");
    if (readOnly) classes.push("saltToggleButtonGroup-readOnly");
    if (className) classes.push(className);

    return (
      <ToggleButtonGroupContext.Provider value={contextValue}>
        <div
          aria-disabled={disabled}
          aria-readonly={readOnly}
          className={classes.join(" ")}
          onKeyDown={handleKeyDown}
          ref={setRefs}
          role="radiogroup"
          {...rest}
        >
          {children}
        </div>
      </ToggleButtonGroupContext.Provider>
    );
  },
);
